import { type FilterOptions } from '@resolute/schema';
import { useMemo } from 'react';


import { useFilterOptions } from './api';

/**
 * Resolves a raw stored value (a property type / city / listing status …) to
 * the label the UI should show, using the live `/filter-options` vocabulary.
 *
 * This replaces the hand-written label maps that used to live in
 * `constants.ts`. Those covered 12 guessed types against CPzone's actual 14 —
 * only 6 overlapped, so 8 live types ("independent-floor",
 * "single-storeyed-home", "shop-showroom" …) rendered as raw slugs on every
 * card, detail page and compare row. The upstream list is the only thing that
 * can stay correct as CPzone's vocabulary changes, so it is the source here.
 *
 * When a value can't be resolved (options still loading, or a value the
 * upstream list doesn't contain) the raw slug is title-cased rather than shown
 * verbatim — so the worst case is "Independent Floor" instead of a bare
 * "independent-floor", and no view ever flashes a lowercase slug.
 */
export interface FilterLabels {
  type: (value: string | undefined) => string;
  city: (value: string | undefined) => string;
  status: (value: string | undefined) => string;
  category: (value: string | undefined) => string;
}

type OptionList = FilterOptions['propertyTypes'] | undefined;

/**
 * Matches on `value` first, then on a case-insensitive `label`.
 *
 * Both forms are genuinely in play for cities: a filter chip holds the CPzone
 * city *id* (`"1"`, which is what the search API takes), while a property card
 * carries the lowercased city *name* (`"ahmedabad"`). One resolver handling
 * both keeps every caller from having to know which shape it holds.
 */
function resolve(list: OptionList, value: string | undefined): string {
  if (!value) return '';
  const byValue = list?.find((o) => o.value === value);
  if (byValue) return byValue.label;
  const lower = value.toLowerCase();
  const byLabel = list?.find((o) => o.label.toLowerCase() === lower);
  return byLabel ? byLabel.label : titleCase(value);
}

/** Last-resort presentation of an unresolved slug: "shop-showroom" -> "Shop
 *  Showroom". Derived from the value itself, so unlike a lookup table it can
 *  never go stale against CPzone's vocabulary. */
function titleCase(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Label resolvers backed by live filter options. Shares the
 * `['filter-options']` query with the filter panel and header menu, so calling
 * this adds no request — including from a list of cards, where every card
 * reads the same cached entry.
 */
export function useFilterLabels(): FilterLabels {
  const { data } = useFilterOptions();
  return useMemo(
    () => ({
      type: (v) => resolve(data?.propertyTypes, v),
      city: (v) => resolve(data?.cities, v),
      status: (v) => resolve(data?.listingStatuses, v),
      category: (v) => resolve(data?.categories, v),
    }),
    [data],
  );
}
